import { Box } from '@mantine/core';
import {
  forwardRef,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Coding,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Reference,
  Task,
} from '@medplum/fhirtypes';
import { QuestionnaireForm, useMedplum, useMedplumProfile } from '@medplum/react';
import { createReference, getReferenceString } from '@medplum/core';
import { QUESTIONNAIRE_REPEAT_GROUP_NUMBERING_EXTENSION } from '@bariendo/lib/extensions';
import { alert } from '@/services/alert';
import { BMI_INPUT_DEBOUNCE_MS } from './debounceTiming';

export type TaskQuestionnaireFormHandle = {
  /** Latest merged response for persist/sign; may lag BMI debounce while typing height/weight. */
  getLatestResponse: () => QuestionnaireResponse | undefined;
};

interface TaskQuestionnaireFormProps {
  task: Task;
  onChangeResponse?: (task: Task, response: QuestionnaireResponse) => void;
}

export const TaskQuestionnaireForm = forwardRef<TaskQuestionnaireFormHandle, TaskQuestionnaireFormProps>(
  function TaskQuestionnaireForm({ task, onChangeResponse }, ref): ReactElement {
    const medplum = useMedplum();
    const author = useMedplumProfile();
    const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>(undefined);
    const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    // Force re-render when BMI is calculated by tracking response version
    const [responseVersion, setResponseVersion] = useState(0);
    // Track the latest response with calculated BMI for immediate form updates
    const latestResponseRef = useRef<QuestionnaireResponse | undefined>(undefined);
    // Debounce timer for BMI calculation
    const bmiCalculationTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    // Track pending response that needs BMI calculation
    const pendingResponseRef = useRef<QuestionnaireResponse | undefined>(undefined);
    // Track if we're currently typing in BMI fields to prevent state updates
    const isTypingBMIFieldsRef = useRef(false);
    const formContainerRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        getLatestResponse: () => latestResponseRef.current ?? questionnaireResponse,
      }),
      [questionnaireResponse]
    );

    // Debounced BMI calculation function
    const calculateBMIDebounced = useCallback(
      (response: QuestionnaireResponse) => {
        // Clear any existing timeout
        if (bmiCalculationTimeoutRef.current) {
          clearTimeout(bmiCalculationTimeoutRef.current);
        }

        // Store the pending response
        pendingResponseRef.current = response;
        isTypingBMIFieldsRef.current = true;

        // Set a new timeout to calculate BMI after user stops typing
        bmiCalculationTimeoutRef.current = setTimeout(() => {
          const pendingResponse = pendingResponseRef.current;
          if (!pendingResponse) {
            isTypingBMIFieldsRef.current = false;
            return;
          }

          const baseResponse = questionnaireResponse || pendingResponse;
          let mergedItems = mergeResponseItems(baseResponse.item ?? [], pendingResponse.item ?? []);

          // Calculate BMI
          mergedItems = calculateBMI(mergedItems);

          const updatedResponse: QuestionnaireResponse = sanitizeQuestionnaireResponse({
            ...baseResponse,
            item: mergedItems,
            status: 'in-progress',
            authored: new Date().toISOString(),
            source: author && createReference(author),
          });

          // Update ref and state after user stopped typing
          latestResponseRef.current = updatedResponse;
          isTypingBMIFieldsRef.current = false;
          setQuestionnaireResponse(updatedResponse);
          setResponseVersion((prev) => prev + 1);
          onChangeResponse?.(task, updatedResponse);
          pendingResponseRef.current = undefined;
        }, BMI_INPUT_DEBOUNCE_MS);
      },
      [questionnaireResponse, author, task, onChangeResponse]
    );

    const onChange = (response: QuestionnaireResponse): void => {
      const baseResponse = questionnaireResponse || response;

      // Merge form response with base response, matching items by id
      // This preserves items that weren't edited (e.g., second "allergies" item)
      let mergedItems = mergeResponseItems(baseResponse.item ?? [], response.item ?? []);

      // Check if this is a BMI questionnaire and if BMI-relevant fields changed
      const isBMIQuestionnaire = questionnaire?.name === 'bmi';
      const hasBMIRelevantFields = isBMIQuestionnaire
        ? response.item?.some(
            (item) =>
              item.linkId === 'bmi-height-ft' || item.linkId === 'bmi-height-in' || item.linkId === 'bmi-weight-lbs'
          )
        : false;

      if (isBMIQuestionnaire && hasBMIRelevantFields) {
        // For BMI fields, only update the ref (not state) to preserve focus
        // The QuestionnaireForm manages its own internal state, so it will show typed values
        // We avoid state updates during typing to prevent re-renders that cause focus loss
        const updatedResponse: QuestionnaireResponse = sanitizeQuestionnaireResponse({
          ...baseResponse,
          item: mergedItems,
          status: 'in-progress',
          authored: new Date().toISOString(),
          source: author && createReference(author),
        });

        // Only update ref - no state update to prevent re-render and focus loss
        latestResponseRef.current = updatedResponse;

        // Schedule BMI calculation after user stops typing
        // State will be updated when debounce completes, which won't cause focus loss
        calculateBMIDebounced(updatedResponse);
      } else {
        // For non-BMI fields or non-BMI questionnaires, update immediately
        // Ensure BMI item exists if it's a BMI questionnaire
        if (isBMIQuestionnaire) {
          const hasBMICalculation = mergedItems.some((item) => item.linkId === 'bmi-calculation');
          if (!hasBMICalculation) {
            mergedItems = calculateBMI(mergedItems);
          }
        }

        const updatedResponse: QuestionnaireResponse = sanitizeQuestionnaireResponse({
          ...baseResponse,
          item: mergedItems,
          status: 'in-progress',
          authored: new Date().toISOString(),
          source: author && createReference(author),
        });

        latestResponseRef.current = updatedResponse;

        queueMicrotask(() => {
          setQuestionnaireResponse(updatedResponse);
          setResponseVersion((prev) => prev + 1);
          onChangeResponse?.(task, updatedResponse);
        });
      }
    };

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (bmiCalculationTimeoutRef.current) {
          clearTimeout(bmiCalculationTimeoutRef.current);
        }
      };
    }, []);

    const questionnaireRefKey = task.input?.[0]?.valueReference
      ? (getReferenceString(task.input[0].valueReference as Reference<Questionnaire>) ?? '')
      : '';
    const questionnaireResponseRefKey = task.output?.[0]?.valueReference
      ? (getReferenceString(task.output[0].valueReference as Reference<QuestionnaireResponse>) ?? '')
      : '';

    useEffect(() => {
      let cancelled = false;

      const fetchResources = async (): Promise<void> => {
        const questionnaireReference = task.input?.[0]?.valueReference as Reference<Questionnaire>;
        const questionnaireResponseReference = task.output?.[0]?.valueReference as Reference<QuestionnaireResponse>;

        const [questionnaireResource, questionnaireResponseResource] = await Promise.all([
          questionnaireReference
            ? medplum.readReference(questionnaireReference)
            : Promise.resolve<Questionnaire | undefined>(undefined),
          questionnaireResponseReference
            ? medplum.readReference(questionnaireResponseReference)
            : Promise.resolve<QuestionnaireResponse | undefined>(undefined),
        ]);

        if (cancelled) {
          return;
        }

        if (questionnaireResource) {
          setQuestionnaire(questionnaireResource as Questionnaire);
        }

        if (questionnaireResponseResource) {
          const normalizedResponse = questionnaireResource
            ? normalizeQuestionnaireResponse(
                questionnaireResource as Questionnaire,
                questionnaireResponseResource as QuestionnaireResponse
              )
            : (questionnaireResponseResource as QuestionnaireResponse);
          setQuestionnaireResponse(normalizedResponse);
          latestResponseRef.current = normalizedResponse;
        } else {
          setQuestionnaireResponse(undefined);
          latestResponseRef.current = undefined;
        }
      };

      setLoading(true);
      fetchResources()
        .catch((err) => {
          if (cancelled) {
            return;
          }
          alert.error({
            title: 'Error - Fetching resources',
            message: `Couldn't fetch resources: ${err instanceof Error ? err.message : 'Unknown error'}`,
          });
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });

      return () => {
        cancelled = true;
      };
      // Re-fetch only when task identity or linked definitions/responses actually change — not when the
      // parent passes a new Task instance after timeline refresh with the same references.
    }, [medplum, task.id, task.status, questionnaireRefKey, questionnaireResponseRefKey]);

    // Calculate BMI and create response with calculated value for display
    // Use ref for immediate updates, fallback to state for initial load
    const responseWithCalculatedBMI = useMemo(() => {
      const responseToUse = latestResponseRef.current || questionnaireResponse;
      if (!responseToUse || questionnaire?.name !== 'bmi') {
        return responseToUse;
      }

      // For BMI questionnaire, only calculate BMI if we're not currently typing
      // This prevents focus loss during typing - form manages its own state during typing
      if (isTypingBMIFieldsRef.current) {
        // Return response without BMI calculation while typing to avoid re-render
        return responseToUse;
      }

      const itemsWithBMI = calculateBMI(responseToUse.item ?? []);
      return {
        ...responseToUse,
        item: itemsWithBMI,
      };
      // Include responseVersion to force recalculation when BMI is updated
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionnaireResponse, questionnaire?.name, responseVersion]);

    // Get BMI value for key to force re-render when it changes
    const bmiValue = useMemo(() => {
      if (questionnaire?.name !== 'bmi' || !responseWithCalculatedBMI?.item) {
        return undefined;
      }
      const bmiItem = responseWithCalculatedBMI.item.find((i) => i.linkId === 'bmi-calculation');
      return bmiItem?.answer?.[0]?.valueDecimal;
    }, [responseWithCalculatedBMI, questionnaire?.name]);

    // Stable identity for Medplum QuestionnaireForm / useQuestionnaireForm → useResource([value]); a new
    // object each render re-triggers that effect and can cause repeated Questionnaire GETs.
    const questionnaireForForm = useMemo((): Questionnaire | undefined => {
      if (!questionnaire) {
        return undefined;
      }
      return { ...questionnaire, title: undefined };
    }, [questionnaire]);

    useEffect(() => {
      if (!questionnaire || task.status === 'completed') {
        return;
      }

      const container = formContainerRef.current;
      if (!container) {
        return;
      }

      const repeatGroupTitles = getNumberedRepeatGroupTitles(questionnaire);
      if (repeatGroupTitles.length === 0) {
        return;
      }

      const applyVisualNumbering = (): void => {
        numberRenderedRepeatGroupTitles(container, repeatGroupTitles);
      };

      applyVisualNumbering();

      const observer = new MutationObserver(() => applyVisualNumbering());
      observer.observe(container, { childList: true, subtree: true, characterData: true });

      return () => observer.disconnect();
    }, [questionnaire, task.status, responseWithCalculatedBMI]);

    if (loading) {
      // TODO: confirm the behavior with design team
      return <div>Loading...</div>;
    }

    return (
      <Box ref={formContainerRef} p={6} mb={20} style={{ border: '1px solid #DEE2E6', borderRadius: '8px' }}>
        {task.status !== 'completed' && questionnaireForForm && (
          <QuestionnaireForm
            key={`${task.id}-${bmiValue ?? 'no-bmi'}`}
            questionnaire={questionnaireForForm}
            questionnaireResponse={responseWithCalculatedBMI}
            excludeButtons={true}
            onChange={onChange}
          />
        )}
      </Box>
    );
  }
);

function normalizeQuestionnaireResponse(
  questionnaire: Questionnaire,
  questionnaireResponse: QuestionnaireResponse
): QuestionnaireResponse {
  return {
    ...questionnaireResponse,
    item: normalizeQuestionnaireResponseItems(questionnaire.item ?? [], questionnaireResponse.item),
  };
}

function normalizeQuestionnaireResponseItems(
  questionnaireItems: QuestionnaireItem[],
  responseItems: QuestionnaireResponseItem[] | undefined
): QuestionnaireResponseItem[] | undefined {
  if (!responseItems) {
    return responseItems;
  }

  return responseItems.map((responseItem) => {
    const questionnaireItem = questionnaireItems.find((item) => item.linkId === responseItem.linkId);
    if (!questionnaireItem) {
      return responseItem;
    }

    const normalizedAnswers = responseItem.answer
      ? responseItem.answer.map((answer) => normalizeAnswer(questionnaireItem, answer))
      : undefined;

    const normalizedChildItems = responseItem.item
      ? normalizeQuestionnaireResponseItems(questionnaireItem.item ?? [], responseItem.item)
      : undefined;

    return {
      ...responseItem,
      ...(normalizedAnswers ? { answer: normalizedAnswers } : {}),
      ...(normalizedChildItems ? { item: normalizedChildItems } : {}),
    };
  });
}

function normalizeAnswer(
  questionnaireItem: QuestionnaireItem,
  answer: QuestionnaireResponseItemAnswer
): QuestionnaireResponseItemAnswer {
  if (!answer.valueCoding || !questionnaireItem.answerOption) {
    return answer;
  }

  const matchedOption = findMatchingAnswerOption(questionnaireItem.answerOption, answer.valueCoding);
  if (!matchedOption?.valueCoding) {
    return answer;
  }

  return {
    ...answer,
    valueCoding: matchedOption.valueCoding,
  };
}

function findMatchingAnswerOption(
  answerOptions: QuestionnaireItemAnswerOption[],
  valueCoding: Coding
): QuestionnaireItemAnswerOption | undefined {
  return answerOptions.find((option) => {
    if (!option.valueCoding) {
      return false;
    }
    return codingsEqual(option.valueCoding, valueCoding);
  });
}

function codingsEqual(codingA: Coding, codingB: Coding): boolean {
  const systemMatches = !codingA.system || !codingB.system || codingA.system === codingB.system;
  const codeMatches =
    (codingA.code !== undefined && codingB.code !== undefined && codingA.code === codingB.code) ||
    (codingA.code === undefined && codingB.code === undefined && !!codingA.display && !!codingB.display);
  const displayMatches =
    !codingA.display || !codingB.display || codingA.display.toLowerCase() === codingB.display.toLowerCase();

  return systemMatches && codeMatches && displayMatches;
}

/**
 * Merges form response items with base response items by matching items by their id.
 * Items that exist in base but not in form are preserved unchanged.
 * Items that exist in form are updated with form data.
 */
function mergeResponseItems(
  baseItems: QuestionnaireResponseItem[],
  formItems: QuestionnaireResponseItem[]
): QuestionnaireResponseItem[] {
  // Create a map of form items by id for quick lookup
  const formItemsById = new Map<string, QuestionnaireResponseItem>();
  // Also create a map by linkId for items without id (like calculated BMI)
  const formItemsByLinkId = new Map<string, QuestionnaireResponseItem>();
  // Track which form items have been merged (by id or linkId)
  const mergedFormItemIds = new Set<string>();
  const mergedFormItemLinkIds = new Set<string>();

  formItems.forEach((item) => {
    if (item.id) {
      formItemsById.set(item.id, item);
    }
    if (item.linkId && !item.id) {
      formItemsByLinkId.set(item.linkId, item);
    }
  });

  // Process each base item - if it has an id that exists in form, update it; otherwise keep it
  const mergedItems: QuestionnaireResponseItem[] = baseItems.map((baseItem) => {
    if (baseItem.id) {
      const formItem = formItemsById.get(baseItem.id);
      if (formItem) {
        // Mark this form item as merged
        mergedFormItemIds.add(baseItem.id);
        // Recursively merge nested items
        const mergedNestedItems =
          baseItem.item || formItem.item ? mergeResponseItems(baseItem.item ?? [], formItem.item ?? []) : undefined;

        return {
          ...formItem,
          id: baseItem.id, // Preserve the id
          linkId: baseItem.linkId, // Preserve the original linkId
          item: mergedNestedItems,
        };
      }
      // If no match by id, try matching by linkId (in case form item doesn't have id)
      if (baseItem.linkId) {
        const formItemByLinkId = formItemsByLinkId.get(baseItem.linkId);
        if (formItemByLinkId) {
          // Mark this form item as merged by linkId
          mergedFormItemLinkIds.add(baseItem.linkId);
          // Recursively merge nested items
          const mergedNestedItems =
            baseItem.item || formItemByLinkId.item
              ? mergeResponseItems(baseItem.item ?? [], formItemByLinkId.item ?? [])
              : undefined;

          return {
            ...formItemByLinkId,
            id: baseItem.id, // Preserve the id from base
            linkId: baseItem.linkId, // Preserve the original linkId
            item: mergedNestedItems,
          };
        }
      }
    } else if (baseItem.linkId) {
      // For items without id, try to match by linkId (e.g., calculated BMI)
      const formItem = formItemsByLinkId.get(baseItem.linkId);
      if (formItem) {
        // Mark this form item as merged by linkId
        mergedFormItemLinkIds.add(baseItem.linkId);
        // Recursively merge nested items
        const mergedNestedItems =
          baseItem.item || formItem.item ? mergeResponseItems(baseItem.item ?? [], formItem.item ?? []) : undefined;

        return {
          ...formItem,
          linkId: baseItem.linkId, // Preserve the original linkId
          // Preserve id if it exists in baseItem
          ...(baseItem.id ? { id: baseItem.id } : {}),
          item: mergedNestedItems,
        };
      }
    }

    // No match found - keep the base item unchanged
    return baseItem;
  });

  // Add any new items from form that don't exist in base and haven't been merged
  formItems.forEach((formItem) => {
    if (formItem.id && !mergedFormItemIds.has(formItem.id) && !baseItems.some((item) => item.id === formItem.id)) {
      mergedItems.push(formItem);
    }
  });

  // Also add items from form that don't have an id but have a linkId (for items like calculated BMI)
  // But only if they haven't been merged and don't already exist in merged items
  formItems.forEach((formItem) => {
    if (!formItem.id && formItem.linkId) {
      const wasMerged = mergedFormItemLinkIds.has(formItem.linkId);
      const existsInMerged = mergedItems.some((item) => item.linkId === formItem.linkId);
      if (!wasMerged && !existsInMerged) {
        mergedItems.push(formItem);
      }
    }
  });

  return mergedItems;
}

function hasAnswerValue(answer: QuestionnaireResponseItemAnswer): boolean {
  return (
    answer.valueBoolean !== undefined ||
    answer.valueDecimal !== undefined ||
    answer.valueInteger !== undefined ||
    answer.valueDate !== undefined ||
    answer.valueDateTime !== undefined ||
    answer.valueTime !== undefined ||
    (answer.valueString !== undefined && answer.valueString.trim() !== '') ||
    answer.valueCoding !== undefined ||
    answer.valueUri !== undefined ||
    answer.valueAttachment !== undefined ||
    answer.valueQuantity !== undefined ||
    answer.valueReference !== undefined
  );
}

function sanitizeResponseItems(
  items: QuestionnaireResponseItem[] | undefined
): QuestionnaireResponseItem[] | undefined {
  if (!items) {
    return items;
  }

  return items.reduce<QuestionnaireResponseItem[]>((acc, item) => {
    const sanitizedAnswers = item.answer?.filter(hasAnswerValue);
    const sanitizedChildren = sanitizeResponseItems(item.item);
    const sanitizedItem: QuestionnaireResponseItem = {
      ...item,
      answer: sanitizedAnswers?.length ? sanitizedAnswers : undefined,
      item: sanitizedChildren,
    };

    const isGroupItem = Array.isArray(item.item);
    if (isGroupItem) {
      const hasOwnAnswers = Boolean(sanitizedItem.answer?.length);
      const hasChildContent = Boolean(sanitizedItem.item?.length);
      if (!hasOwnAnswers && !hasChildContent) {
        return acc;
      }
    }

    acc.push(sanitizedItem);
    return acc;
  }, []);
}

function sanitizeQuestionnaireResponse(response: QuestionnaireResponse): QuestionnaireResponse {
  return {
    ...response,
    item: sanitizeResponseItems(response.item),
  };
}

/**
 * Calculates BMI from height and weight fields and updates the BMI calculation field.
 * Formula: BMI = (weight in lbs) / (height in inches)^2 * 703
 */
function calculateBMI(items: QuestionnaireResponseItem[]): QuestionnaireResponseItem[] {
  // First, remove any duplicate bmi-calculation items (keep the first one with an id if available)
  const deduplicatedItems: QuestionnaireResponseItem[] = [];
  let bmiCalculationItem: QuestionnaireResponseItem | undefined;

  for (const item of items) {
    if (item.linkId === 'bmi-calculation') {
      if (!bmiCalculationItem) {
        // Keep the first bmi-calculation item we find
        bmiCalculationItem = item;
        deduplicatedItems.push(item);
      }
      // Skip any subsequent duplicates
    } else {
      deduplicatedItems.push(item);
    }
  }

  // Helper to find and extract numeric value from a response item
  const findNumericValue = (linkId: string): number | undefined => {
    const item = deduplicatedItems.find((i) => i.linkId === linkId);
    if (!item?.answer || item.answer.length === 0) {
      return undefined;
    }
    const answer = item.answer[0];
    if (answer.valueInteger !== undefined) {
      return answer.valueInteger;
    }
    if (answer.valueDecimal !== undefined) {
      return answer.valueDecimal;
    }
    return undefined;
  };

  const heightFt = findNumericValue('bmi-height-ft');
  const heightIn = findNumericValue('bmi-height-in');
  const weightLbs = findNumericValue('bmi-weight-lbs');

  // Calculate BMI if we have all required values
  let bmiValue: number | undefined;
  if (heightFt !== undefined && heightIn !== undefined && weightLbs !== undefined) {
    // Convert height to total inches: (feet * 12) + inches
    const totalHeightInches = heightFt * 12 + heightIn;

    if (totalHeightInches > 0) {
      bmiValue = (weightLbs / (totalHeightInches * totalHeightInches)) * 703;
      // Round to 1 decimal place
      bmiValue = Math.round(bmiValue * 10) / 10;
    }
  }

  // Update or create the BMI calculation item
  // Preserve object references for items that don't change to avoid losing focus
  const updatedItems = deduplicatedItems.map((item) => {
    if (item.linkId === 'bmi-calculation') {
      // Check if the BMI value actually changed
      const currentBMIValue = item.answer?.[0]?.valueDecimal;
      const bmiValueChanged = currentBMIValue !== bmiValue;

      if (bmiValueChanged) {
        return {
          ...item,
          answer: bmiValue !== undefined ? [{ valueDecimal: bmiValue }] : [],
        };
      }
      // Value hasn't changed, return the same object reference to preserve focus
      return item;
    }
    // Not a BMI item, return the same object reference
    return item;
  });

  // If BMI calculation item doesn't exist, create it
  const hasBMICalculationItem = updatedItems.some((item) => item.linkId === 'bmi-calculation');
  if (!hasBMICalculationItem) {
    // Always add the BMI item, even if value is undefined, so the form knows about it
    updatedItems.push({
      linkId: 'bmi-calculation',
      text: 'BMI',
      ...(bmiCalculationItem?.id ? { id: bmiCalculationItem.id } : {}),
      answer: bmiValue !== undefined ? [{ valueDecimal: bmiValue }] : [],
    });
  }

  return updatedItems;
}

const VISUAL_NUMBERED_TITLE_SELECTOR = 'legend,label,h1,h2,h3,h4,h5,h6,p,span,div,strong';

function getNumberedRepeatGroupTitles(questionnaire: Questionnaire): string[] {
  const titles = new Set<string>();
  collectNumberedRepeatGroupTitles(questionnaire.item ?? [], titles);
  return [...titles];
}

function collectNumberedRepeatGroupTitles(questionnaireItems: QuestionnaireItem[], titles: Set<string>): void {
  for (const item of questionnaireItems) {
    if (item.type === 'group' && item.repeats === true && item.text && shouldNumberRepeatGroup(item)) {
      titles.add(item.text);
    }

    if (item.item?.length) {
      collectNumberedRepeatGroupTitles(item.item, titles);
    }
  }
}

function numberRenderedRepeatGroupTitles(container: HTMLElement, repeatGroupTitles: string[]): void {
  if (repeatGroupTitles.length === 0) {
    return;
  }

  const indexedTitleCounts = new Map<string, number>();
  repeatGroupTitles.forEach((title) => indexedTitleCounts.set(title, 0));

  const elements = container.querySelectorAll<HTMLElement>(VISUAL_NUMBERED_TITLE_SELECTOR);
  elements.forEach((element) => {
    if (element.children.length > 0) {
      return;
    }

    const originalText = element.textContent?.trim();
    if (!originalText) {
      return;
    }

    for (const title of repeatGroupTitles) {
      const titleRegex = new RegExp(`^${escapeRegExp(title)}(?:\\s+#\\d+)?$`);
      if (!titleRegex.test(originalText)) {
        continue;
      }

      const currentCount = (indexedTitleCounts.get(title) ?? 0) + 1;
      indexedTitleCounts.set(title, currentCount);

      const nextText = `${title} #${currentCount}`;
      if (originalText !== nextText) {
        element.textContent = nextText;
      }
      break;
    }
  });
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function shouldNumberRepeatGroup(item: QuestionnaireItem): boolean {
  return (
    item.extension?.some(
      (extension) => extension.url === QUESTIONNAIRE_REPEAT_GROUP_NUMBERING_EXTENSION && extension.valueBoolean === true
    ) ?? false
  );
}
